import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  ArrowLeft,
  ShoppingBag,
  User,
  MapPin,
  Calendar,
  Package,
  FileText,
  Loader2,
  Pencil,
  CheckCircle,
  Clock,
  XCircle,
  Truck,
  Printer,
  Mail,
  Phone,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CounterpartyBadge } from '@/components/trading/sales/counterparty-badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { cn } from '@/lib/utils';

const statusConfig: Record<string, { color: string; icon: typeof Clock; label: string }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Draft' },
  CONFIRMED: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Confirmed' },
  PARTIALLY_SHIPPED: { color: 'bg-purple-100 text-purple-700', icon: Package, label: 'Partially Shipped' },
  FULLY_SHIPPED: { color: 'bg-cyan-100 text-cyan-700', icon: Truck, label: 'Fully Shipped' },
  DELIVERED: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Delivered' },
  CANCELLED: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' },
};

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('SALES_ORDERS', 'EDIT');
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const { data: order, isLoading, error } = trpc.tradingSalesOrder.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const utils = trpc.useUtils();

  const confirmMutation = trpc.tradingSalesOrder.confirm.useMutation({
    onSuccess: () => {
      utils.tradingSalesOrder.byId.invalidate(id!);
      showSuccessToast(t('sales.orders.confirmed'));
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const cancelMutation = trpc.tradingSalesOrder.cancel.useMutation({
    onSuccess: () => {
      utils.tradingSalesOrder.byId.invalidate(id!);
      showSuccessToast(t('sales.orders.cancelled'));
      setCancelConfirmOpen(false);
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const formatCurrency = (value: number | { toNumber: () => number }, currency: string = 'EUR') => {
    const numValue = typeof value === 'object' ? value.toNumber() : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(numValue);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/sales/orders')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('sales.orders.backToList')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('sales.orders.notFound')}</p>
        </div>
      </div>
    );
  }

  const config = statusConfig[order.status] ?? statusConfig.DRAFT!;
  const StatusIcon = config.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/sales/orders')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('sales.orders.backToList')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center bg-violet-100">
              <ShoppingBag className="h-6 w-6 text-violet-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold font-mono">{order.orderNumber}</h1>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                    config.color
                  )}
                >
                  <StatusIcon className="h-3 w-3" />
                  {t(`sales.orders.${order.status.toLowerCase()}`)}
                </span>
              </div>
              <p className="text-muted-foreground">
                {t('sales.orders.createdOn')} {formatDate(order.createdAt)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => navigate(`/trading/sales/orders/${id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              {tCommon('edit')}
            </Button>
          )}
          {canEdit && order.status === 'DRAFT' && (
            <>
              <Button onClick={() => confirmMutation.mutate(id!)}>
                {confirmMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <CheckCircle className="mr-2 h-4 w-4" />
                {t('sales.orders.confirm')}
              </Button>
            </>
          )}
          {canEdit && (order.status === 'CONFIRMED' || order.status === 'PARTIALLY_SHIPPED') && (
            <Button onClick={() => navigate(`/trading/sales/deliveries/new?soId=${id}`)}>
              <Truck className="mr-2 h-4 w-4" />
              {t('sales.orders.ship')}
            </Button>
          )}
          {canEdit && order.status !== 'CANCELLED' && order.status !== 'DELIVERED' && (
            <Button variant="outline" onClick={() => setCancelConfirmOpen(true)}>
              <XCircle className="mr-2 h-4 w-4" />
              {t('sales.orders.cancel')}
            </Button>
          )}
          <Button variant="outline">
            <Printer className="mr-2 h-4 w-4" />
            {tCommon('print')}
          </Button>
        </div>
      </div>

      {/* Cancel Confirmation */}
      <ConfirmationDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title={t('sales.orders.cancelOrder')}
        description={t('sales.orders.cancelConfirm')}
        confirmLabel={t('sales.orders.cancel')}
        variant="destructive"
        onConfirm={() => cancelMutation.mutate(id!)}
        isLoading={cancelMutation.isPending}
      />

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">{t('sales.orders.tabs.details')}</TabsTrigger>
          <TabsTrigger value="items">{t('sales.orders.tabs.items')}</TabsTrigger>
          <TabsTrigger value="deliveries">{t('sales.orders.tabs.deliveries')}</TabsTrigger>
          <TabsTrigger value="history">{t('sales.orders.tabs.history')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Customer Info */}
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <User className="h-5 w-5" />
                {t('sales.orders.customer')}
                <CounterpartyBadge kind={(order as any).counterpartyKind} className="ml-2" />
              </h2>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('sales.orders.company')}</span>
                  <p className="font-medium">{order.companyName || '-'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('sales.orders.contactName')}</span>
                  <p className="font-medium">{order.contactName}</p>
                </div>
                {order.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${order.email}`} className="text-primary hover:underline">
                      {order.email}
                    </a>
                  </div>
                )}
                {order.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${order.phone}`} className="hover:underline">
                      {order.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <MapPin className="h-5 w-5" />
                {t('sales.orders.shippingAddress')}
              </h2>
              <div className="text-sm">
                {order.shippingAddress || order.shippingCity || order.shippingCountry ? (
                  <div className="space-y-1">
                    {order.shippingAddress && <p>{order.shippingAddress}</p>}
                    <p>
                      {[order.shippingCity, order.shippingCountry].filter(Boolean).join(', ')}
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">{t('sales.orders.noAddress')}</p>
                )}
              </div>
            </div>

            {/* Order Details */}
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <Calendar className="h-5 w-5" />
                {t('sales.orders.orderDetails')}
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.orderDate')}</span>
                  <span className="font-medium">{formatDate(order.orderDate)}</span>
                </div>
                {order.requiredDate && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.orders.requiredDate')}</span>
                    <span className="font-medium">{formatDate(order.requiredDate)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.orders.currency')}</span>
                  <span className="font-medium">{order.currency}</span>
                </div>
                {order.paymentTerms && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.orders.paymentTerms')}</span>
                    <span className="font-medium">{order.paymentTerms}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Package className="h-5 w-5" />
              {t('sales.orders.summary')}
            </h2>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="text-center p-4 bg-muted/30">
                <p className="text-2xl font-bold">{order.items?.length || 0}</p>
                <p className="text-sm text-muted-foreground">{t('sales.orders.items')}</p>
              </div>
              <div className="text-center p-4 bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(order.subtotal, order.currency)}</p>
                <p className="text-sm text-muted-foreground">{t('sales.orders.subtotal')}</p>
              </div>
              <div className="text-center p-4 bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(order.taxAmount, order.currency)}</p>
                <p className="text-sm text-muted-foreground">{t('sales.orders.tax')}</p>
              </div>
              <div className="text-center p-4 bg-violet-100">
                <p className="text-2xl font-bold text-violet-700">{formatCurrency(order.total, order.currency)}</p>
                <p className="text-sm text-violet-600">{t('sales.orders.total')}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <FileText className="h-5 w-5" />
                {t('sales.orders.notes')}
              </h2>
              <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <div className="border border-border bg-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('sales.orders.product')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('sales.orders.warehouse')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.quantity')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.unitPrice')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.discount')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.tax')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.lineTotal')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {order.items?.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-sm">{item.productName || '-'}</p>
                        <p className="text-xs text-muted-foreground font-mono">{item.productCode || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{item.warehouseName || '-'}</td>
                    <td className="px-4 py-3 text-right text-sm">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm">{formatCurrency(item.unitPrice, order.currency)}</td>
                    <td className="px-4 py-3 text-right text-sm">{item.discountPercent}%</td>
                    <td className="px-4 py-3 text-right text-sm">{item.taxRate}%</td>
                    <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(item.lineTotal, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td colSpan={5} />
                  <td className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.subtotal')}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(order.subtotal, order.currency)}</td>
                </tr>
                <tr>
                  <td colSpan={5} />
                  <td className="px-4 py-3 text-right text-sm font-medium">{t('sales.orders.tax')}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(order.taxAmount, order.currency)}</td>
                </tr>
                <tr className="bg-violet-50">
                  <td colSpan={5} />
                  <td className="px-4 py-3 text-right text-sm font-bold">{t('sales.orders.total')}</td>
                  <td className="px-4 py-3 text-right text-sm font-bold">{formatCurrency(order.total, order.currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="deliveries" className="space-y-4">
          {order.deliveries && order.deliveries.length > 0 ? (
            <div className="border border-border bg-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t('sales.deliveries.deliveryNumber')}</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">{t('sales.deliveries.status')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t('sales.deliveries.shipDate')}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.deliveries.items')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">{t('sales.deliveries.trackingNumber')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {order.deliveries.map((delivery) => (
                    <tr key={delivery.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/sales/deliveries/${delivery.id}`}
                          className="font-mono text-sm font-medium text-primary hover:underline"
                        >
                          {delivery.deliveryNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={cn(
                          'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                          delivery.status === 'DELIVERED' ? 'bg-green-100 text-green-700' :
                          delivery.status === 'SHIPPED' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        )}>
                          {delivery.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{delivery.shipDate ? formatDate(delivery.shipDate) : '-'}</td>
                      <td className="px-4 py-3 text-right text-sm">{delivery.itemCount || 0}</td>
                      <td className="px-4 py-3 text-sm font-mono">{delivery.trackingNumber || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center border border-border bg-muted/30">
              <Truck className="h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-medium">{t('sales.deliveries.noDeliveries')}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('sales.deliveries.noDeliveriesDesc')}
              </p>
              {canEdit && (order.status === 'CONFIRMED' || order.status === 'PARTIALLY_SHIPPED') && (
                <Button className="mt-4" onClick={() => navigate(`/trading/sales/deliveries/new?soId=${id}`)}>
                  <Truck className="mr-2 h-4 w-4" />
                  {t('sales.orders.ship')}
                </Button>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <div className="border border-border bg-card p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center bg-blue-100">
                  <Clock className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-sm">{t('sales.orders.created')}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(order.createdAt)}</p>
                </div>
              </div>
              {order.status !== 'DRAFT' && order.status !== 'CANCELLED' && (
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-green-100">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t('sales.orders.confirmed')}</p>
                  </div>
                </div>
              )}
              {order.status === 'CANCELLED' && (
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center bg-red-100">
                    <XCircle className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t('sales.orders.cancelled')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default OrderDetailPage;
